import * as CL from '@design-system/component-library';
import { isBefore, subWeeks } from 'date-fns';
import { Link } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { useSearchParams } from '../../common/hooks/useSearchParams.js';
import { billingAccountMsg, companyMsg, t, typeMsg } from '../../common/i18n/index.js';
import { getCompanyName } from '../../common/utils/accountUtils.js';
import { formatTimestampToDDMMYYYY } from '../../common/utils/dateUtils.js';
import { formatSumWithSignum } from '../../common/utils/priceUtils.js';
import { ConsolidatedListView } from '../../components/ConsolidatedListView/ConsolidatedListView.js';
import { DocumentHeader } from '../../generated/api/models.js';
import { getInvoiceDocumentPdfPrivateMethod } from '../../generated/api/uiApiMethods.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { Grid } from '../Grid/Grid.js';
import { Icon } from '../Icon/Icon.js';
import { DEFAULT_ITEMS_PER_PAGE, ListPagination, ListSearch, Table, TableDescription } from '../Table/index.js';
import { setDefaultItemsPerPage } from '../Table/tableUtils.js';
import type { InvoiceDocumentsSearchResponse } from '../../generated/api/models.js';
import type { DefaultListSearchParams } from '../Table/index.js';

import './InvoiceDocumentList.scss';

interface InvoiceDocumentListProps {
  documentSearchResults: InvoiceDocumentsSearchResponse[];
  total: number;
}

export const getTranslation = (documentType: DocumentHeader.DocumentTypeEnum) => {
  switch (documentType) {
    case DocumentHeader.DocumentTypeEnum.CREDIT_NOTE:
      return t.FCH5('Credit note');
    case DocumentHeader.DocumentTypeEnum.FA_STATEMENT:
      return t.QGCA('Financial statement');
    case DocumentHeader.DocumentTypeEnum.PAYMENT_ARRANGEMENT:
      return t.H103('Payment arrangement confirmation');
    case DocumentHeader.DocumentTypeEnum.REMINDER_LETTER:
      return t.Z6YC('Request for payment');
    case DocumentHeader.DocumentTypeEnum.TERMINATION_LETTER:
      return t.P9Z5('Termination letter');
    default:
      return documentType;
  }
};

export const InvoiceDocumentList = ({ documentSearchResults, total }: InvoiceDocumentListProps) => {
  const { authenticatedUser } = useAuth();
  const searchParams = useSearchParams<DefaultListSearchParams>();
  const twoWeeksAgo = subWeeks(Date.now(), 2).getTime();

  const columns = [
    { key: 'documentType', label: t.F9G1(typeMsg), sortable: true },
    { key: 'invoiceDocumentId', label: t.IFT9(billingAccountMsg), sortable: true },
    { key: 'sum', label: t.P4RQ('Amount'), sortable: true },
    { key: 'created', label: t.T3N9('Date'), sortable: true },
    { key: 'companyName', label: t.KJTS(companyMsg), sortable: false },
  ];

  const rows =
    documentSearchResults?.map(documentSearchResult => {
      const document = documentSearchResult.result;
      const isNew = isBefore(twoWeeksAgo, document.created);
      return {
        documentType: (
          <Link
            rel="noopener noreferrer"
            target="_blank"
            to={getInvoiceDocumentPdfPrivateMethod(document.invoiceDocumentId).path}
          >
            <Icon color="blue-600" icon="file-pdf" size="l" type="filled" />
            {getTranslation(document.documentType)}
            <span className={dsClass.SR_ONLY}>&nbsp;PDF</span>
          </Link>
        ),
        invoiceDocumentId: <TableDescription title={document.billingAccountDisplayId || '-'} />,
        sum: <TableDescription title={document.sum ? formatSumWithSignum(document.sum) : '-'} />,
        created: <TableDescription title={formatTimestampToDDMMYYYY(document.created)} />,
        companyName: (
          <div className="badge-col">
            <div>{getCompanyName(authenticatedUser, document.accountMasterId)}</div>
            {isNew && (
              <div className="badge">
                <CL.Badge color="blue-subtle" text={t.SX1L('New')} />
              </div>
            )}
          </div>
        ),
      };
    }) || [];

  return (
    <Grid>
      <div className="of-invoice-documents">
        <ConsolidatedListView
          authenticatedUser={authenticatedUser}
          listSearch={
            <ListSearch
              className={dsClass.MARGIN_TOP_4}
              onSearch={() => setDefaultItemsPerPage(new URLSearchParams(searchParams), DEFAULT_ITEMS_PER_PAGE)}
            />
          }
          listElement={<Table columns={columns} noItemsText={t.BOIN('No invoice documents')} rows={rows} />}
          listPagination={<ListPagination totalItems={total} />}
          wideLayout={true}
        />
      </div>
    </Grid>
  );
};
